import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import Popup from '../components/Popup';

/* ─── styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .bs-page {
    font-family: 'Inter', system-ui, sans-serif;
    min-height: 100vh;
    background: #0a0f1e;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 40px 20px 80px;
    position: relative;
    overflow: hidden;
  }

  .bs-blob {
    position: fixed; border-radius: 50%; filter: blur(80px);
    pointer-events: none; z-index: 0;
  }
  .bs-blob-1 { width: 500px; height: 500px; background: rgba(99,102,241,0.09); top: -150px; left: -150px; }
  .bs-blob-2 { width: 400px; height: 400px; background: rgba(236,160,28,0.07); bottom: -100px; right: -100px; }

  .bs-container {
    display: flex; gap: 30px; width: 100%; max-width: 1100px;
    align-items: flex-start; justify-content: center;
    position: relative; z-index: 1;
    animation: bsFadeUp 0.45s ease-out both;
  }
  @media (max-width: 900px) {
    .bs-container { flex-direction: column; align-items: center; }
  }

  .bs-card, .bs-list-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.18);
    border-radius: 24px;
    padding: 48px 40px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.4);
    backdrop-filter: blur(18px);
    width: 100%; box-sizing: border-box;
  }
  .bs-card { flex: 1.2; max-width: 580px; }
  .bs-list-card { flex: 1; max-width: 480px; padding: 40px 32px; }

  @keyframes bsFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* header */
  .bs-logo {
    display: inline-flex; align-items: center; justify-content: center;
    width: 48px; height: 48px;
    background: linear-gradient(135deg, #f59e0b, #d97706);
    border-radius: 12px; font-size: 1.3rem; color: white;
    box-shadow: 0 0 20px rgba(245,158,11,0.4); margin-bottom: 20px;
  }
  .bs-title    { font-size: 1.75rem; font-weight: 900; color: #f1f5f9; letter-spacing: -0.03em; margin: 0 0 6px; }
  .bs-subtitle { font-size: 14px; color: #64748b; margin: 0 0 32px; }

  .bs-plan-badge {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
    padding: 3px 10px; border-radius: 100px; margin-bottom: 24px;
  }
  .bs-plan-badge-pro      { color: #818cf8; background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.25); }
  .bs-plan-badge-business { color: #fbbf24; background: rgba(251,191,36,0.1); border: 1px solid rgba(251,191,36,0.2); }

  /* slot bar */
  .bs-slot-bar-wrap { margin-bottom: 24px; }
  .bs-slot-label { font-size: 13px; color: #64748b; margin-bottom: 8px; display: flex; justify-content: space-between; }
  .bs-slot-label strong { color: #94a3b8; }
  .bs-slot-track { height: 6px; background: rgba(99,102,241,0.12); border-radius: 100px; overflow: hidden; }
  .bs-slot-fill  { height: 100%; border-radius: 100px; background: linear-gradient(90deg, #818cf8, #6366f1); transition: width 0.4s ease; }
  .bs-slot-fill-warn { background: linear-gradient(90deg, #f59e0b, #d97706); }
  .bs-slot-fill-full { background: linear-gradient(90deg, #ef4444, #dc2626); }

  /* alerts */
  .bs-alert { border-radius: 12px; padding: 14px 16px; font-size: 14px; margin-bottom: 22px; line-height: 1.55; }
  .bs-alert-error   { background: rgba(239,68,68,0.1);  border: 1px solid rgba(239,68,68,0.25);  color: #fca5a5; }
  .bs-alert-success { background: rgba(34,197,94,0.1);  border: 1px solid rgba(34,197,94,0.2);   color: #86efac; }
  .bs-alert-warning { background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.2);  color: #fcd34d; }
  .bs-alert-info    { background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2);  color: #a5b4fc; }

  /* brand list */
  .bs-brand-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 28px; }
  .bs-brand-item {
    display: flex; align-items: center; justify-content: space-between;
    background: rgba(99,102,241,0.06); border: 1px solid rgba(99,102,241,0.15);
    border-radius: 12px; padding: 12px 16px; gap: 12px;
  }
  .bs-brand-item-name { font-size: 15px; font-weight: 700; color: #fbbf24; flex: 1; }
  .bs-brand-item-urls { font-size: 12px; color: #64748b; margin-top: 2px; }
  .bs-brand-item-left { display: flex; flex-direction: column; }
  .bs-brand-delete {
    background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2);
    color: #fca5a5; border-radius: 8px; padding: 6px 12px;
    font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.2s;
    white-space: nowrap; flex-shrink: 0;
  }
  .bs-brand-delete:hover { background: rgba(239,68,68,0.18); }

  .bs-empty { text-align: center; padding: 28px; color: #475569; font-size: 14px; }

  /* pagination */
  .bs-pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 20px; }
  .bs-page-btn {
    background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.3);
    color: #a5b4fc; padding: 6px 14px; border-radius: 8px; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: all 0.2s; font-family: inherit;
  }
  .bs-page-btn:hover:not(:disabled) { background: rgba(99,102,241,0.2); }
  .bs-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .bs-page-info { font-size: 13px; color: #94a3b8; }

  /* add new brand section */
  .bs-add-section {
    background: rgba(255,255,255,0.02); border: 1px solid rgba(99,102,241,0.12);
    border-radius: 16px; padding: 20px; margin-bottom: 24px;
  }
  .bs-add-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; margin: 0 0 14px; }

  /* form */
  .bs-form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
  .bs-label { font-size: 13px; font-weight: 700; color: #94a3b8; margin-bottom: 6px; display: block; }

  .bs-input-wrap { position: relative; }
  .bs-input {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(99,102,241,0.2);
    border-radius: 10px;
    padding: 13px 44px 13px 16px;
    font-size: 15px; color: #e2e8f0;
    font-family: inherit; outline: none;
    transition: border-color 0.2s, background 0.2s;
    width: 100%; box-sizing: border-box;
  }
  .bs-input::placeholder { color: #334155; }
  .bs-input:focus { border-color: rgba(99,102,241,0.5); background: rgba(99,102,241,0.05); }
  .bs-input.available   { border-color: rgba(34,197,94,0.5);  }
  .bs-input.unavailable { border-color: rgba(239,68,68,0.5);  }

  .bs-check-icon {
    position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
    font-size: 16px; pointer-events: none;
  }

  .bs-availability { font-size: 12px; margin-top: 6px; letter-spacing: 0.01em; }
  .bs-avail-ok  { color: #4ade80; }
  .bs-avail-bad { color: #f87171; }
  .bs-avail-checking { color: #94a3b8; }

  /* preview section */
  .bs-preview-card {
    background: rgba(99,102,241,0.06);
    border: 1px solid rgba(99,102,241,0.15);
    border-radius: 14px;
    padding: 14px 16px;
    margin-bottom: 16px;
  }
  .bs-preview-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; margin: 0 0 10px; }
  .bs-preview-row {
    display: flex; align-items: center; gap: 10px;
    padding: 6px 0;
    border-bottom: 1px solid rgba(99,102,241,0.08);
  }
  .bs-preview-row:last-child { border-bottom: none; }
  .bs-preview-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #475569; min-width: 70px; }
  .bs-preview-url   { font-size: 12px; color: #818cf8; font-weight: 500; word-break: break-all; }
  .bs-preview-url .brand-part { color: #fbbf24; font-weight: 700; }

  /* save button */
  .bs-save {
    width: 100%; padding: 13px;
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: white; border: none; border-radius: 12px;
    font-size: 14px; font-weight: 700; cursor: pointer;
    font-family: inherit; transition: all 0.2s;
    box-shadow: 0 4px 20px rgba(245,158,11,0.35);
  }
  .bs-save:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(245,158,11,0.5); }
  .bs-save:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }

  .bs-back {
    width: 100%; padding: 12px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(99,102,241,0.18);
    color: #64748b; border-radius: 12px;
    font-size: 14px; font-weight: 600; cursor: pointer;
    font-family: inherit; transition: all 0.2s;
  }
  .bs-back:hover { background: rgba(99,102,241,0.1); color: #a5b4fc; border-color: rgba(99,102,241,0.3); }

  .bs-divider { width: 100%; height: 1px; background: rgba(99,102,241,0.1); margin: 16px 0 20px; }

  /* upgrade banner */
  .bs-upgrade {
    background: rgba(251,191,36,0.08);
    border: 1px solid rgba(251,191,36,0.2);
    border-radius: 16px; padding: 28px; text-align: center;
  }
  .bs-upgrade-icon { font-size: 2.5rem; margin-bottom: 12px; }
  .bs-upgrade h3 { font-size: 1.15rem; font-weight: 800; color: #f1f5f9; margin: 0 0 8px; }
  .bs-upgrade p  { font-size: 14px; color: #64748b; margin: 0 0 20px; }
  .bs-upgrade-btn {
    display: inline-block; padding: 12px 28px;
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: white; text-decoration: none;
    border-radius: 10px; font-weight: 700; font-size: 14px;
    transition: all 0.2s; box-shadow: 0 4px 16px rgba(245,158,11,0.35);
  }
  .bs-upgrade-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(245,158,11,0.5); }

  .bs-pro-upgrade-tip {
    background: rgba(99,102,241,0.06); border: 1px solid rgba(99,102,241,0.15);
    border-radius: 12px; padding: 14px 16px; font-size: 13px; color: #818cf8;
    margin-bottom: 20px; text-align: center;
  }
  .bs-pro-upgrade-tip a { color: #a5b4fc; font-weight: 700; text-decoration: none; }
  .bs-pro-upgrade-tip a:hover { text-decoration: underline; }

  @media (max-width: 480px) {
    .bs-card { padding: 36px 20px; }
  }
`;

/* ─── main component ─────────────────────────────────────────────────────── */
const BrandSettings = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [brands, setBrands] = useState([]);
    const [brandLimit, setBrandLimit] = useState(0);
    const [brandInput, setBrandInput] = useState('');
    const [availability, setAvailability] = useState(null); // null | 'checking' | { available, reason }
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    // Popup state
    const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', type: 'confirm', onConfirm: null });

    const ITEMS_PER_PAGE = 8;
    const checkTimeoutRef = useRef(null);

    const plan = user?.plan;
    const canUseBrand = plan === 'pro' || plan === 'business';
    const isBusiness = plan === 'business';

    // Fetch brands on mount
    useEffect(() => {
        if (!canUseBrand) { setLoading(false); return; }
        axios.get(`${API_BASE_URL}/api/brand`)
            .then(r => {
                setBrands(r.data.brands || []);
                setBrandLimit(r.data.limit || 0);
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [canUseBrand]);

    // Debounced availability check
    useEffect(() => {
        if (!brandInput || brandInput.length < 3) {
            setAvailability(null);
            return;
        }
        // Already own this brand name
        if (brands.some(b => b.brand_name === brandInput)) {
            setAvailability({ available: false, reason: 'You already own this brand name' });
            return;
        }
        setAvailability('checking');
        clearTimeout(checkTimeoutRef.current);
        checkTimeoutRef.current = setTimeout(async () => {
            try {
                const r = await axios.post(`${API_BASE_URL}/api/brand/check`, { brand_name: brandInput });
                setAvailability(r.data);
            } catch {
                setAvailability(null);
            }
        }, 500);
        return () => clearTimeout(checkTimeoutRef.current);
    }, [brandInput, brands]);

    const handleBrandInput = (val) => {
        setBrandInput(val.toLowerCase().replace(/[^a-z0-9-]/g, ''));
        setError(null);
        setSuccess(null);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        setError(null); setSuccess(null);
        setSaving(true);
        try {
            await axios.post(`${API_BASE_URL}/api/brand`, { brand_name: brandInput });
            const r = await axios.get(`${API_BASE_URL}/api/brand`);
            setBrands(r.data.brands || []);
            setBrandInput('');
            setAvailability(null);
            setSuccess('Brand name added successfully!');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add brand name');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (name) => {
        setPopup({
            isOpen: true,
            title: 'Remove Brand',
            message: `Are you sure you want to remove the brand "${name}"? All links using this brand will be permanently deleted. This action cannot be undone.`,
            type: 'danger',
            onConfirm: async () => {
                setPopup(p => ({ ...p, isOpen: false }));
                setError(null); setSuccess(null);
                try {
                    await axios.delete(`${API_BASE_URL}/api/brand/${encodeURIComponent(name)}`);
                    setBrands(prev => prev.filter(b => b.brand_name !== name));
                    setSuccess(`Brand name "${name}" removed.`);
                } catch (err) {
                    setError(err.response?.data?.error || 'Failed to remove brand name');
                }
            }
        });
    };

    const usedCount = brands.length;
    const remaining = brandLimit - usedCount;
    const atLimit = usedCount >= brandLimit;
    const fillPct = brandLimit > 0 ? Math.min(100, (usedCount / brandLimit) * 100) : 0;
    const fillClass = fillPct >= 100 ? 'bs-slot-fill bs-slot-fill-full' : fillPct >= 80 ? 'bs-slot-fill bs-slot-fill-warn' : 'bs-slot-fill';

    const totalPages = Math.max(1, Math.ceil(usedCount / ITEMS_PER_PAGE));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const paginatedBrands = brands.slice((safeCurrentPage - 1) * ITEMS_PER_PAGE, safeCurrentPage * ITEMS_PER_PAGE);

    const displayBrand = brandInput || 'yourbrand';
    const canSave = availability?.available === true && brandInput.length >= 3 && !saving && !atLimit;

    return (
        <div className="bs-page">
            <style>{STYLES}</style>
            <div className="bs-blob bs-blob-1" />
            <div className="bs-blob bs-blob-2" />

            <div className="bs-container">
                <div className="bs-card">
                    <div className="bs-logo">🏷️</div>
                    <h1 className="bs-title">Brand Settings</h1>
                    <p className="bs-subtitle">Customise how your links appear with your brand identity</p>

                    {canUseBrand ? (
                        <>
                            <span className={`bs-plan-badge ${isBusiness ? 'bs-plan-badge-business' : 'bs-plan-badge-pro'}`}>
                                {isBusiness ? '⭐ Business Feature' : '✦ Pro Feature'}
                            </span>

                            {/* Alerts */}
                            {error && <div className="bs-alert bs-alert-error">{error}</div>}
                            {success && <div className="bs-alert bs-alert-success">✅ {success}</div>}

                            {/* Add new brand */}
                            {!atLimit && (
                                <div className="bs-add-section">
                                    <p className="bs-add-title">Add New Brand Name</p>
                                    <form onSubmit={handleAdd}>
                                        <div className="bs-form-group">
                                            <label className="bs-label" htmlFor="bs-brand">Brand Name</label>
                                            <div className="bs-input-wrap">
                                                <input
                                                    id="bs-brand"
                                                    type="text"
                                                    className={`bs-input${availability?.available === true ? ' available' : availability?.available === false ? ' unavailable' : ''}`}
                                                    placeholder="yourbrand"
                                                    value={brandInput}
                                                    onChange={e => handleBrandInput(e.target.value)}
                                                    maxLength={30}
                                                />
                                                {availability === 'checking' && <span className="bs-check-icon">⏳</span>}
                                                {availability?.available === true && <span className="bs-check-icon">✅</span>}
                                                {availability?.available === false && <span className="bs-check-icon">❌</span>}
                                            </div>
                                            {availability === 'checking' && <p className="bs-availability bs-avail-checking">Checking availability…</p>}
                                            {availability?.available === true && <p className="bs-availability bs-avail-ok">✓ Available!</p>}
                                            {availability?.available === false && <p className="bs-availability bs-avail-bad">✗ {availability.reason}</p>}
                                            <p style={{ fontSize: 12, color: '#475569', marginTop: 6 }}>
                                                3–30 characters. Lowercase letters, numbers, and hyphens only.
                                            </p>
                                        </div>

                                        {/* Live preview */}
                                        <div className="bs-preview-card">
                                            <p className="bs-preview-title">URL Preview</p>
                                            <div className="bs-preview-row">
                                                <span className="bs-preview-label">Standard</span>
                                                <span className="bs-preview-url">rdrto.com/<strong>abcd</strong></span>
                                            </div>
                                            <div className="bs-preview-row">
                                                <span className="bs-preview-label">Path</span>
                                                <span className="bs-preview-url">rdrto.com/<span className="brand-part">{displayBrand}</span>/abcd</span>
                                            </div>
                                            <div className="bs-preview-row">
                                                <span className="bs-preview-label">Subdomain</span>
                                                <span className="bs-preview-url"><span className="brand-part">{displayBrand}</span>.rdrto.com/abcd</span>
                                            </div>
                                        </div>

                                        <button type="submit" className="bs-save" disabled={!canSave}>
                                            {saving ? 'Adding…' : `➕ Add Brand Name (${remaining} slot${remaining === 1 ? '' : 's'} left)`}
                                        </button>
                                    </form>
                                </div>
                            )}

                            {/* Upgrade tip for Pro users at limit */}
                            {!isBusiness && atLimit && (
                                <div className="bs-pro-upgrade-tip">
                                    You've used all 5 Pro brand names.{' '}
                                    <Link to="/upgrade">Upgrade to Business</Link> to get up to 100.
                                </div>
                            )}

                            <div className="bs-divider" />
                            <button className="bs-back" onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
                        </>
                    ) : (
                        <div className="bs-upgrade">
                            <div className="bs-upgrade-icon">🚀</div>
                            <h3>Pro or Business Plan Required</h3>
                            <p>Brand name customisation is available on the Pro plan (up to 5 brands) and Business plan (up to 100 brands). Upgrade to create branded short links that showcase your identity.</p>
                            <Link to="/upgrade" className="bs-upgrade-btn">View Upgrade Options</Link>
                        </div>
                    )}
                </div>

                {canUseBrand && (
                    <div className="bs-list-card">
                        <h2 className="bs-title" style={{ fontSize: '1.4rem' }}>Your Brands</h2>
                        <p className="bs-subtitle" style={{ marginBottom: '24px' }}>Manage your existing brand names</p>

                        {/* Slot usage bar */}
                        <div className="bs-slot-bar-wrap">
                            <div className="bs-slot-label">
                                <span>Brand names used</span>
                                <strong>{usedCount} / {brandLimit}</strong>
                            </div>
                            <div className="bs-slot-track">
                                <div className={fillClass} style={{ width: `${fillPct}%` }} />
                            </div>
                        </div>

                        {/* Existing brand list */}
                        {loading ? (
                            <div className="bs-empty">Loading…</div>
                        ) : brands.length === 0 ? (
                            <div className="bs-empty">No brand names yet. Add your first one on the left.</div>
                        ) : (
                            <>
                                <div className="bs-brand-list" style={{ marginBottom: '10px' }}>
                                    {paginatedBrands.map(b => (
                                        <div key={b.brand_name} className="bs-brand-item">
                                            <div className="bs-brand-item-left">
                                                <span className="bs-brand-item-name" style={{ fontSize: '14px' }}>🏷️ {b.brand_name}</span>
                                                <span className="bs-brand-item-urls" style={{ fontSize: '11px' }}>
                                                    rdrto.com/{b.brand_name}/slug<br />{b.brand_name}.rdrto.com/slug
                                                </span>
                                            </div>
                                            <button className="bs-brand-delete" onClick={() => handleDelete(b.brand_name)}>
                                                🗑️ Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="bs-pagination">
                                        <button
                                            className="bs-page-btn"
                                            disabled={safeCurrentPage === 1}
                                            onClick={() => setCurrentPage(p => p - 1)}
                                        >
                                            ← Prev
                                        </button>
                                        <span className="bs-page-info">Page {safeCurrentPage} of {totalPages}</span>
                                        <button
                                            className="bs-page-btn"
                                            disabled={safeCurrentPage === totalPages}
                                            onClick={() => setCurrentPage(p => p + 1)}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            <Popup
                isOpen={popup.isOpen}
                title={popup.title}
                message={popup.message}
                type={popup.type}
                onConfirm={popup.onConfirm}
                onCancel={() => setPopup(p => ({ ...p, isOpen: false }))}
            />
        </div>
    );
};

export default BrandSettings;
